import { Box, Typography, Button } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Table } from '@components/table/Table';
import { InView } from 'react-intersection-observer';
import { getQuoteActivityColumns } from './getQuoteActivityColumns';
import { useQuoteActivityHelper } from '@pages/QuoteDetails/sidebarItems/summary/useQuoteActivityHelper';
import { ClockFormat } from '@lio/rule-engine';
import { useStore } from '@store/GlobalStore';
import { shallow } from 'zustand/shallow';
import { SearchField } from '@components/search';
import { Menu, MenuItemType } from '@components/menu';
import { SlidersH, CaretDown } from '@assets/svgs';
import { MouseEvent } from 'react';
import { Sorting } from '@pages/QuoteDetails/sidebarItems/summary/quoteActivity/QuoteActivity.constants';
import { underwritingTabStyles } from '@pages/UnderwriterToolkit/tab-content/UnderwritingTab.styles';

interface QuoteActivityPanelProps {
  quoteId: string;
}

export const QuoteActivityPanel = ({ quoteId }: QuoteActivityPanelProps) => {
  const {
    items,
    isLoading,
    isFetchingMore,
    setIsScrolledToBottom,
    sorting,
    filter,
    textInput,
    setSorting,
    setFilter,
    setTextInput,
  } = useQuoteActivityHelper(quoteId);
  const { isInternalUser } = useStore((state) => state.userAuth, shallow);
  const theme = useTheme();
  const columns = getQuoteActivityColumns({ clockFormat: ClockFormat.TwelveHour, isInternalUser, theme });

  const handleOpenFilter = (event: MouseEvent<HTMLButtonElement>) =>
    setFilter({ ...filter, anchorEl: event.currentTarget });

  const handleCloseFilter = () => setFilter({ ...filter, anchorEl: null });

  const handleFilterItemClick = (_: number, innerText?: string) => {
    setFilter({
      ...filter,
      list: filter.list.map((item: any) => ({
        ...item,
        checked: item.text === innerText ? !item.checked : item.checked,
      })),
    });
  };

  const handleOpenSorting = (event: MouseEvent<HTMLButtonElement>) =>
    setSorting({ ...sorting, anchorEl: event.currentTarget });

  const handleCloseSorting = () => setSorting({ ...sorting, anchorEl: null });

  const handleSortingItemClick = (_: number, innerText?: string) => {
    setSorting({
      ...sorting,
      type: innerText === 'Newest First' ? Sorting.Newest : Sorting.Oldest,
    });
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Fixed header section */}
      <Box sx={{ flexShrink: 0 }}>
        <SearchField
          autofocus
          overrideValue={textInput}
          onChange={(e) => setTextInput(e)}
          onPressEndIcon={() => setTextInput('')}
          variant='standard'
          sx={{ margin: '16px auto', width: '90%' }}
        />
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            borderBottom: '1px solid rgba(0, 0, 0, 0.15)',
            mb: 2,
          }}
        >
          <Button variant='text' onClick={handleOpenFilter} startIcon={<SlidersH />}>
            Filters
          </Button>
          <Menu
            anchorEl={filter.anchorEl}
            open={Boolean(filter.anchorEl)}
            items={filter.list}
            onClose={handleCloseFilter}
            onClickMenuItem={handleFilterItemClick}
          />
          <Button variant='text' onClick={handleOpenSorting} endIcon={<CaretDown />}>
            {sorting.type}
          </Button>
          <Menu
            anchorEl={sorting.anchorEl}
            open={Boolean(sorting.anchorEl)}
            items={[
              { type: MenuItemType.Default, text: 'Newest First' },
              { type: MenuItemType.Default, text: 'Oldest First' },
            ]}
            onClose={handleCloseSorting}
            onClickMenuItem={handleSortingItemClick}
          />
        </Box>
      </Box>

      {/* Scrollable table section */}
      <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
        <Table
          ariaLabel='Quote Activity Table'
          columns={columns}
          onLoadData={() => ({
            data: {
              items: items,
              pagination: { total: items.length },
            },
            isLoading: false,
            isFetching: false,
            isError: false,
          })}
          rowConfig={{ getRowHeight: () => 56 }}
          hidePagination={true}
          sxProps={{
            ...underwritingTabStyles,
            tableContainer: {
              height: '100%',
              overflow: 'auto',
              '& .MuiTableHead-root': {
                position: 'sticky',
                top: 0,
                zIndex: 1,
                backgroundColor: 'background.paper',
              },
            },
          }}
        />
        {isFetchingMore && (
          <Box sx={{ display: 'flex', justifyContent: 'center', padding: 2, flexShrink: 0 }}>
            <Typography variant='bodySmall'>Loading more...</Typography>
          </Box>
        )}
        <InView
          as='div'
          onChange={(inView) => setIsScrolledToBottom(inView)}
          style={{ height: '20px', flexShrink: 0 }}
        />
      </Box>
    </Box>
  );
};
